import { Cart } from '@bao/cart'
import { BAOCustomElement } from '@bao/custom-element'
import { eventBus, EVENTS } from '@bao/events'
import { sectionRenderer } from '@bao/section-rendering'
import { getElement } from '@bao/utils'

class PushCartError {
  /**
   * @param {Object} error
   *
   * @returns {void}
   */
  static async handle (error) {
    const instance = new this(error)

    if (instance.genericError) {
      return instance.render(instance.MESSAGES.GENERIC)
    } else if (instance.soldOut) {
      return instance.render(instance.MESSAGES.SOLD_OUT)
    } else if (instance.alreadyHaveAllInCart) {
      return instance.render(instance.MESSAGES.ALREADY_HAVE_ALL_STOCK)
    } else if (instance.addedMoreThanExists) {
      return instance.render(instance.MESSAGES.NOT_ENOUGH_IN_STOCK)
    } else if (instance.cannotFindVariant) {
      return instance.render(instance.MESSAGES.CANNOT_FIND_VARIANT)
    } else {
      if (typeof error?.message === 'string') {
        return instance.render(error.message)
      } else if (typeof error?.errors === 'string') {
        return instance.render(error.errors)
      }

      return instance.render(instance.MESSAGES.ADD_TOO_MANY)
    }
  }

  constructor (error) {
    this.error = error

    this.MESSAGES = {
      ADD_TOO_MANY: 'Only the available quantity was added to your bag.',
      ALREADY_HAVE_ALL_STOCK:
        'You already have all available stock in your bag.',
      CANNOT_FIND_VARIANT: 'This product could not be added to your bag.',
      GENERIC: 'Something went wrong while adding this product to your bag.',
      NOT_ENOUGH_IN_STOCK:
        'There isn’t enough stock available for this quantity.',
      SOLD_OUT: 'This product is currently sold out.',
    }

    this.description = error?.description ?? ''
    this.status = error?.status ?? null

    /**
     * @type {PushCart}
     * @public
     */
    this.pushCart = getElement('push-cart')
  }

  render (message) {
    const clone =
      this.pushCart.els['error-template'].element.content.cloneNode(true)
    const text = getElement(this.selectors.text, {
      context: clone,
    })

    text.textContent = message

    this.pushCart.els.errors.element.appendChild(clone)

    return this
  }

  get addedMoreThanExists () {
    return this.description.slice(0, 18) === "You can't add more"
  }

  get alreadyHaveAllInCart () {
    return this.description.slice(0, 4) === 'All '
  }

  get genericError () {
    return this.error?.errors?.toLowerCase() === ' error'
  }

  get soldOut () {
    return this.description.slice(0, 13) === "The product '"
  }

  get cannotFindVariant () {
    return this.description.toLowerCase() === 'cannot find variant'
  }

  get selectors () {
    return {
      item: '[data-push-cart-errors-item]',
      text: '[data-push-cart-errors-text]',
      close: '[data-push-cart-errors-close]',
    }
  }
}

export class PushCart extends BAOCustomElement() {
  static get requiredElements () {
    return ['error-template', 'errors']
  }

  _loading = false

  get loading () {
    return this._loading
  }

  set loading (val) {
    this._loading = val

    this.classList.toggle(this.loadingClass, val)
    this.setAttribute('aria-busy', val)
  }

  constructor () {
    super()

    this.sections = sectionRenderer.pushCartSections

    if (this.drawer == null) {
      this.throwError('needs to be used within a <site-drawer> element')
    }
  }

  setupListeners () {
    super.setupListeners()

    this.onBeforeCartAdd = this.onBeforeCartAdd.bind(this)
    this.onBeforeClearCartAttributes =
      this.onBeforeClearCartAttributes.bind(this)
    this.onCartChanged = this.onCartChanged.bind(this)
    this.onAfterCartAdd = this.onAfterCartAdd.bind(this)
    this.onRemoveAllClick = this.onRemoveAllClick.bind(this)

    /* Before events */
    eventBus.addEventListener(
      EVENTS.PUSH_CART.BEFORE_ADD,
      this.onBeforeCartAdd,
    )
    eventBus.addEventListener(
      EVENTS.PUSH_CART.BEFORE_MULTIPLE_ADD,
      this.onBeforeCartAdd,
    )
    eventBus.addEventListener(
      EVENTS.PUSH_CART.BEFORE_ADD_FROM_FORM,
      this.onBeforeCartAdd,
    )
    eventBus.addEventListener(
      EVENTS.PUSH_CART.BEFORE_CLEAR_CART_ATTRIBUTES,
      this.onBeforeClearCartAttributes,
    )

    /* Changed event */
    eventBus.addEventListener(EVENTS.PUSH_CART.CHANGED, this.onCartChanged)

    /* After events */
    eventBus.addEventListener(EVENTS.PUSH_CART.AFTER_ADD, this.onAfterCartAdd)
    eventBus.addEventListener(
      EVENTS.PUSH_CART.AFTER_MULTIPLE_ADD,
      this.onAfterCartAdd,
    )
    eventBus.addEventListener(
      EVENTS.PUSH_CART.AFTER_ADD_FROM_FORM,
      this.onAfterCartAdd,
    )
    eventBus.addEventListener(
      EVENTS.PUSH_CART.AFTER_CLEAR_CART_ATTRIBUTES,
      this.onAfterCartAdd,
    )

    /* Remove all */
    this.drawer.addEventListener('click', this.onRemoveAllClick)
  }

  disconnectedCallback () {
    super.disconnectedCallback()

    /* Before events */
    eventBus.removeEventListener(
      EVENTS.PUSH_CART.BEFORE_ADD,
      this.onBeforeCartAdd,
    )
    eventBus.removeEventListener(
      EVENTS.PUSH_CART.BEFORE_MULTIPLE_ADD,
      this.onBeforeCartAdd,
    )
    eventBus.removeEventListener(
      EVENTS.PUSH_CART.BEFORE_ADD_FROM_FORM,
      this.onBeforeCartAdd,
    )
    eventBus.removeEventListener(
      EVENTS.PUSH_CART.BEFORE_CLEAR_CART_ATTRIBUTES,
      this.onBeforeClearCartAttributes,
    )

    /* Changed event */
    eventBus.removeEventListener(EVENTS.PUSH_CART.CHANGED, this.onCartChanged)

    /* After events */
    eventBus.removeEventListener(
      EVENTS.PUSH_CART.AFTER_ADD,
      this.onAfterCartAdd,
    )
    eventBus.removeEventListener(
      EVENTS.PUSH_CART.AFTER_MULTIPLE_ADD,
      this.onAfterCartAdd,
    )
    eventBus.removeEventListener(
      EVENTS.PUSH_CART.AFTER_ADD_FROM_FORM,
      this.onAfterCartAdd,
    )
    eventBus.removeEventListener(
      EVENTS.PUSH_CART.AFTER_CLEAR_CART_ATTRIBUTES,
      this.onAfterCartAdd,
    )

    /* Remove all */
    this.drawer.removeEventListener('click', this.onRemoveAllClick)
  }

  onBeforeCartAdd () {
    window.drawers.activeDrawerKey = 'cart'

    this.loading = true
  }

  onBeforeClearCartAttributes () {
    this.loading = true
  }

  onCartChanged (e) {
    const { state } = e.detail
    if (!state) return
    if (!state.sections) return

    if (!('push-cart-count' in state.sections)) return

    const newCartTotal = parseInt(
      this.getCount(state.sections['push-cart-count']),
    )

    this.externalEls.drawer?.element.classList.toggle(
      'drw-Drawer-pushCartEmpty',
      newCartTotal === 0,
    )

    if (newCartTotal === 0) {
      this.externalEls.checkout?.element.setAttribute(
        'disabled',
        newCartTotal === 0,
      )
    } else {
      this.externalEls.checkout?.element.removeAttribute('disabled')
    }
  }

  onAfterCartAdd (event) {
    if (event.detail.state?.sections) {
      this.renderSections(event.detail.state.sections)
    }

    if (this.externalEls?.scroll?.exists) {
      const prefersReducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)',
      ).matches

      this.externalEls.scroll.element.scrollTo({
        top: 0,
        left: 0,
        behavior: prefersReducedMotion ? 'instant' : 'smooth',
      })
    }

    this.loading = false
  }

  onRemoveAllClick (event) {
    const trigger = event.target.closest(
      '[data-push-cart-external-el="remove-all"]',
    )

    if (!trigger) return

    this.onRemoveAll()
  }

  async onRemoveAll () {
    if (this.loading) return

    this.loading = true

    try {
      const state = await Cart.clearCart()

      if (state?.sections) {
        this.renderSections(state.sections)
      }
    } catch (error) {
      PushCartError.handle({
        message: error?.message ?? 'There was an error clearing your cart.',
      })
    } finally {
      this.loading = false
    }
  }

  /**
   * @param {string} html
   *
   * @returns {string}
   */
  getCount (html) {
    const DOM = sectionRenderer.convertToDOM(html)

    return DOM.querySelector('#push-cart-count').textContent
  }

  /**
   * @param {Response} res
   *
   * @returns {void}
   */
  async handleError (res) {
    this.loading = false

    return PushCartError.handle(await res.json())
  }

  get drawer () {
    return this.closest('[is="site-drawer"]')
  }

  get loadingClass () {
    return this.getAttribute('loading-class')
  }
}

if (!customElements.get('push-cart')) {
  customElements.define('push-cart', PushCart)
}
